import React, { useEffect, useRef, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { classNames } from "primereact/utils";
import { useThemeContext } from "../../../../contexts/ThemeContext/ThemeContext";
import localeText from "../../../../locale";
import "./DashboardSidebar.css";
import defaultLogo from "../../../../pages/images/logo-color-horizontal.png";
import DiagnosisProgressCard from "../../pages/BusinessDiagnosis/components/DiagnosisProgressCard";
import CompanyDiagnosisService from "../../../../service/CompanyDiagnosisService";
import { getFedernetDashboardUrl } from "../../../../utilities/federnet";
import { logout } from "../../../../utilities/logout";

const DashboardSidebar = ({
  menuItems,
  userInfo,
  userTypeInfo,
  isMenuVisible,
  onMenuItemClick,
  onMenuButtonClick,
  showBusinessSquare = false,
}) => {
  const { subnetInfo } = useThemeContext();
  const history = useHistory();
  const location = useLocation();
  const { logoUrl } = useThemeContext();
  const showMyStore = location.pathname.startsWith("/dashboard-store");
  const canAccessStoreSettings = userTypeInfo !== "Customer";
  const isStore = userTypeInfo === "Store";
  const logo = logoUrl || defaultLogo;

  const isDesktop = () => {
    return window.innerWidth > 896;
  };

  const getInitials = (firstname, surname) => {
    const first = firstname?.charAt(0)?.toUpperCase() || "";
    const second = surname?.charAt(0)?.toUpperCase() || "";
    return `${first}${second}` || "U";
  };

  const handleProfileClick = () => {
    history.push("/dashboard-editprofile");
  };

  const menuVisible = isMenuVisible();
  const desktop = isDesktop();

  const sidebarClassName = classNames("dashboard-sidebar", {
    "dashboard-sidebar-mobile-active": menuVisible && !desktop,
  });
  const isPathPrefixActive = (prefix) => (_, currentLocation) =>
    currentLocation?.pathname?.startsWith(prefix);

  const [diagnosisProgressStep, setDiagnosisProgressStep] = useState(null);
  const [diagnosisService] = useState(() => new CompanyDiagnosisService());
  const latestProgressUpdateRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const loadProgress = async () => {
      /*  if (!showBusinessSquare) {
        setDiagnosisProgressStep(null);
        return;
      } */

      const token = localStorage.getItem("__extraconomy_token");
      if (!token) return;

      const requestStartedAt = Date.now();
      try {
        const res = await diagnosisService.getAllDiagnoses(token);
        const list = res?.result ?? res?.diagnoses ?? res;
        const raw = Array.isArray(list) ? list[0] : list;
        const payload = raw?.company_diagnosis ?? raw;

        if (!payload || typeof payload !== "object") return;

        const statusRaw = payload?.meta?.status;
        const status = typeof statusRaw === "string" ? statusRaw.trim().toUpperCase() : statusRaw;

        if (status === "COMPLETED") return;

        if (!status || status === "DRAFT") {
          if (!cancelled && requestStartedAt >= latestProgressUpdateRef.current) {
            setDiagnosisProgressStep(1);
          }
          return;
        }

        if (typeof status === "string" && /^STEP\d+$/.test(status)) {
          const stepNum = Number(status.replace("STEP", ""));
          const completedPhase = Math.min(Math.max(stepNum, 1), 8);
          if (!cancelled && requestStartedAt >= latestProgressUpdateRef.current) {
            setDiagnosisProgressStep(completedPhase);
          }
          return;
        }

        const stepSections = [
          "company_registry",
          "company_structure",
          "company_size",
          "human_capital_gender",
          "human_capital_roles",
          "human_capital",
          "representations",
          "other_info",
        ];

        const completed = new Set();
        stepSections.forEach((section, i) => {
          const sectionData = payload?.[section];
          if (
            sectionData &&
            typeof sectionData === "object" &&
            Object.keys(sectionData).length > 0
          ) {
            completed.add(i + 1);
          }
        });

        const firstIncompleteIdx = stepSections.findIndex((_, i) => !completed.has(i + 1));
        const nextStep = firstIncompleteIdx === -1 ? null : firstIncompleteIdx + 1;
        if (!cancelled && requestStartedAt >= latestProgressUpdateRef.current) {
          setDiagnosisProgressStep(nextStep);
        }
      } catch (e) {
        if (!cancelled && requestStartedAt >= latestProgressUpdateRef.current) {
          setDiagnosisProgressStep(null);
        }
      }
    };

    loadProgress();
    return () => {
      cancelled = true;
    };
  }, [showBusinessSquare, diagnosisService]);

  useEffect(() => {
    if (!showBusinessSquare) return;

    const handler = (e) => {
      const statusRaw = e?.detail?.status;
      const status = typeof statusRaw === "string" ? statusRaw.trim().toUpperCase() : statusRaw;

      latestProgressUpdateRef.current = Date.now();

      if (status === "COMPLETED") {
        setDiagnosisProgressStep(null);
        return;
      }

      if (!status || status === "DRAFT") {
        setDiagnosisProgressStep(1);
        return;
      }

      if (typeof status === "string" && /^STEP\d+$/.test(status)) {
        const stepNum = Number(status.replace("STEP", ""));
        const completedPhase = Math.min(Math.max(stepNum, 1), 8);
        setDiagnosisProgressStep(completedPhase);
        return;
      }
    };

    window.addEventListener("businessDiagnosisProgressUpdated", handler);
    return () => {
      window.removeEventListener("businessDiagnosisProgressUpdated", handler);
    };
  }, [showBusinessSquare]);

  return (
    <div className={sidebarClassName}>
      <div className="dashboard-sidebar-logo">
        {/* {!desktop && onMenuButtonClick && (
          <button
            className="dashboard-sidebar-menu-toggle"
            onClick={onMenuButtonClick}
            aria-label="Toggle menu"
          >
            <img src={logo} alt="menu-icon" />
          </button>
        )} */}
        <button className="p-link text-center" onClick={() => history.push("/dashboard")}>
          <img src={logo} alt="extraconomy-logo" />
        </button>
      </div>
      <hr className="mb-2 mt-0 mx-3" />
      <div className="dashboard-sidebar-menu">
        <div className="dashboard-sidebar-menu-section">
          <h3 className="dashboard-sidebar-section-title">PRINCIPAL</h3>
          <ul className="dashboard-sidebar-menu-list">
            {menuItems.map((item, index) => {
              const hasSubItems = item.items && item.items.length > 0;
              const hasDirectLink = item.to;
              if (hasSubItems || hasDirectLink) {
                return (
                  <li key={item.label || index} className="dashboard-sidebar-menu-item">
                    <NavLink
                      to={item.to || item.items?.[0]?.to || "#"}
                      isActive={item.getIsActive}
                      className="dashboard-sidebar-menu-link"
                      activeClassName="dashboard-sidebar-menu-link-active"
                      onClick={(e) => {
                        if (onMenuItemClick) {
                          onMenuItemClick({ originalEvent: e, item });
                        }
                      }}
                    >
                      <i className={item.icon} />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                );
              }
              return null;
            })}
          </ul>
        </div>
        {showMyStore && (
          <div className="dashboard-sidebar-menu-section">
            <h3 className="dashboard-sidebar-section-title">{localeText("myStore")}</h3>
            <ul className="dashboard-sidebar-menu-list">
              {canAccessStoreSettings && (
                <li className="dashboard-sidebar-menu-item">
                  <NavLink
                    to="/dashboard-store/settings"
                    className="dashboard-sidebar-menu-link"
                    activeClassName="dashboard-sidebar-menu-link-active"
                    isActive={isPathPrefixActive("/dashboard-store/settings")}
                  >
                    <i className="pi pi-cog"></i>
                    <span>{localeText("storeSettings")}</span>
                  </NavLink>
                </li>
              )}
              <li className="dashboard-sidebar-menu-item">
                <NavLink
                  to="/dashboard-store/orders"
                  className="dashboard-sidebar-menu-link"
                  activeClassName="dashboard-sidebar-menu-link-active"
                  isActive={isPathPrefixActive("/dashboard-store/orders")}
                >
                  <i className="pi pi-clock"></i>
                  <span>{localeText("storeOrders")}</span>
                </NavLink>
              </li>
              <li className="dashboard-sidebar-menu-item">
                <NavLink
                  to="/dashboard-store/products"
                  className="dashboard-sidebar-menu-link"
                  activeClassName="dashboard-sidebar-menu-link-active"
                  isActive={isPathPrefixActive("/dashboard-store/products")}
                >
                  <i className="pi pi-shopping-bag"></i>
                  <span>{localeText("storeProducts")}</span>
                </NavLink>
              </li>
              <li className="dashboard-sidebar-menu-item">
                <NavLink
                  to="/dashboard-store/schedules"
                  className="dashboard-sidebar-menu-link"
                  activeClassName="dashboard-sidebar-menu-link-active"
                  isActive={isPathPrefixActive("/dashboard-store/schedules")}
                >
                  <i className="pi pi-calendar"></i>
                  <span>{localeText("storeSchedules")}</span>
                </NavLink>
              </li>
              {isStore && (
                <li className="dashboard-sidebar-menu-item">
                  <NavLink
                    to="/dashboard-store/physical-store"
                    className="dashboard-sidebar-menu-link"
                    activeClassName="dashboard-sidebar-menu-link-active"
                    isActive={isPathPrefixActive("/dashboard-store/physical-store")}
                  >
                    <i className="pi pi-building"></i>
                    <span>{localeText("physicalStore")}</span>
                  </NavLink>
                </li>
              )}
            </ul>
          </div>
        )}
        {showBusinessSquare && (
          <div className="dashboard-sidebar-menu-section">
            <h3 className="dashboard-sidebar-section-title">{subnetInfo?.name}</h3>
            <ul className="dashboard-sidebar-menu-list">
              <li className="dashboard-sidebar-menu-item">
                <NavLink
                  to="/dashboard-partner-stores"
                  className="dashboard-sidebar-menu-link"
                  activeClassName="dashboard-sidebar-menu-link-active"
                  exact
                >
                  <i className="pi pi-building"></i>
                  <span>{localeText("partnerStoresMenuLabel") || "Lojas conveniadas"}</span>
                </NavLink>
              </li>
              <li className="dashboard-sidebar-menu-item">
                <NavLink
                  to="/dashboard-welfare"
                  className="dashboard-sidebar-menu-link"
                  activeClassName="dashboard-sidebar-menu-link-active"
                  exact
                >
                  <i className="pi pi-heart"></i>
                  <span>Welfare</span>
                </NavLink>
              </li>
              {Number(subnetInfo?.id) === 2 && (
                <li className="dashboard-sidebar-menu-item">
                  <NavLink
                    to="/dashboard-my-cards"
                    className="dashboard-sidebar-menu-link"
                    activeClassName="dashboard-sidebar-menu-link-active"
                    exact
                  >
                    <i className="pi pi-credit-card"></i>
                    <span>{localeText("myCards")}</span>
                  </NavLink>
                </li>
              )}
              {/* <li className="dashboard-sidebar-menu-item">
                <NavLink
                  to="/dashboard-business-diagnosis"
                  className="dashboard-sidebar-menu-link"
                  activeClassName="dashboard-sidebar-menu-link-active"
                  isActive={isPathPrefixActive("/dashboard-business-diagnosis")}
                >
                  <i className="pi pi-clipboard"></i>
                  <span>{localeText("businessDiagnosis") || "Diagnóstico Empresarial"}</span>
                </NavLink>
              </li> */}
            </ul>
          </div>
        )}
      </div>
      {diagnosisProgressStep && (
        <DiagnosisProgressCard
          currentStep={diagnosisProgressStep}
          totalSteps={8}
          onContinue={() => history.push("/dashboard-store/settings?tab=businessDiagnosis")}
        />
      )}
      {subnetInfo && (
        <div className="px-3">
          <a href={getFedernetDashboardUrl()} className="dashboard-sidebar-menu-link">
            <i className="pi pi-arrow-left"></i>
            <span>{localeText("backToFedernet")}</span>
          </a>
        </div>
      )}
      <hr className="mb-0 mt-2 mx-3" />
      {userInfo && (
        <div className="dashboard-sidebar-profile" onClick={handleProfileClick}>
          <div className="dashboard-sidebar-profile-avatar">
            {userInfo.urlAvatar ? (
              <img src={userInfo.urlAvatar} alt="user-avatar" />
            ) : (
              <div className="dashboard-sidebar-profile-initials">
                {getInitials(userInfo.firstname, userInfo.surname)}
              </div>
            )}
          </div>
          <div className="dashboard-sidebar-profile-info">
            <div className="dashboard-sidebar-profile-name">
              {userInfo.firstname} {userInfo.surname}
            </div>
            <div className="dashboard-sidebar-profile-role">{userTypeInfo}</div>
          </div>
          <button
            type="button"
            className="dashboard-sidebar-logout"
            onClick={(e) => {
              e.stopPropagation();
              logout();
            }}
            title={localeText("logout")}
            aria-label={localeText("logout")}
          >
            <i className="pi pi-sign-out" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};

export default DashboardSidebar;
